import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import ErrorToast, { showToast } from '../../../components/messages/ErrorAlert';
import { useCompanyContext } from '../../../context/CompanyContext';
import LoadingButton from '../../../components/inputs/LoadingButton';
import JobPhasesForm, {
  JobPhasesFormFields,
} from '../../../features/accounting/forms/JobPhases/JobPhasesForm';
import SuccessFormMessage from '../../../components/messages/SuccessFormMessage';
import JobPhasesMessage from '../../../features/accounting/forms/JobPhases/JobPhasesSucessMessage';
import {
  JobPhaseCodesArgs,
  postPhaseCodes,
} from '../../../services/accounting/ACFormsService';
import { AxiosError } from 'axios';
import { useMutation } from '@tanstack/react-query';
import ConfirmationDialog from '../../../components/messages/ConfirmationDialog';

const JobPhases = () => {
  const { selectedCompany } = useCompanyContext();
  const navigate = useNavigate();
  const formMethods = useForm<JobPhasesFormFields>({
    defaultValues: { costCodes: [{}] },
  });
  const { handleSubmit } = formMethods;
  const toast = useRef<Toast>(null);

  const { mutate, isLoading, isSuccess, reset, data } = useMutation({
    mutationFn: (data: JobPhaseCodesArgs) => {
      return postPhaseCodes(selectedCompany!, data);
    },
    onError: (error: AxiosError) => {
      const response = error.response;
      const data = response?.data as { message: string };
      showToast(
        'error',
        toast,
        'Upload Job Phase Codes Error',
        data?.message || 'An error ocurred! please try again later',
        10000
      );
    },
  });

  const onSubmit = (data: JobPhasesFormFields) => {
    mutate(data as JobPhaseCodesArgs);
  };

  useEffect(() => {
    formMethods.reset();
    reset();
  }, [selectedCompany, formMethods, reset]);

  return (
    <div className="flex flex-column justify-content-center gap-5 pt-4">
      <div className="text-35px font-bold text-center mb-2 relative">
        New Job Phase Codes
      </div>
      <ErrorToast toastRef={toast} />
      {!isSuccess && (
        <FormProvider {...formMethods}>
          <form className="max-w-900px mx-auto flex flex-column gap-5">
            <JobPhasesForm />
            <div className="flex flex-wrap flex-row-reverse gap-2 mb-3">
              <ConfirmationDialog
                Button={
                  <LoadingButton
                    isLoading={isLoading}
                    label="Create Phases"
                    className="w-15rem bluwaiBlue border-0 flex m-auto py-3"
                    fontSize="text-22px"
                    type="button"
                  />
                }
                onConfirm={handleSubmit(onSubmit)}
                message="Are you sure you want to submit the job phase codes?"
              />
              <Button
                onClick={() => {
                  navigate('/accounting-dashboard/new');
                }}
                className="w-15rem bluwaiRed border-0 flex m-auto py-3"
                type="button"
              >
                <span className="mx-auto text-22px text-white">Cancel</span>
              </Button>
            </div>
          </form>
        </FormProvider>
      )}
      {isSuccess && (
        <div>
          <SuccessFormMessage
            message={
              <JobPhasesMessage
                job={data.job.Job}
                jobName={data.job.Description}
                costCodes={data.costCodes}
              />
            }
            redirectUrl="/accounting-dashboard/new"
            redirectMessage="Click here to return to the forms list"
          />
        </div>
      )}
    </div>
  );
};

export default JobPhases;
